<script setup lang="ts">
import {computed, ref, watch} from "vue";
import LegacyAccess from "@/core/interfaces/Access";
import Rank from "@/core/interfaces/Rank";
import {ReferentLaboratory, TeamMemberSchedule} from "@/core/interfaces/TeamPlanning";
import {PlanningProfileUpdateRequest} from "@/core/http/resources/TeamPlanningResource";
import {CONTRACT_TYPES, contractLabel, formatHours, jobRoleForRank, jobRoleOption, parseWeeklyHours, rankRequiresRpps} from "@/team/planning";
import {teamPlanningApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import ChipListEditor from "@/team/components/ChipListEditor.vue";
import LaboratoryPicker from "@/team/components/LaboratoryPicker.vue";
import MemberAccessSettings from "@/team/components/MemberAccessSettings.vue";
import useAppointmentTypes from "@/agenda/composables/useAppointmentTypes";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Switch} from "@/core/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {BadgeCheck, FlaskConical, Pencil, ShieldAlert} from "@lucide/vue";

const {member, schedule, organizationId} = defineProps<{
    member: LegacyAccess;
    schedule: TeamMemberSchedule | null;
    organizationId: number;
    team: LegacyAccess[];
    ranks: Rank[];
    operatorCodeOptions: { label: string; value: string }[];
}>();

const emits = defineEmits(['schedule-updated', 'refresh', 'on-remove-operator-code']);
const {showSuccess, showError} = useToastStore();
const {data: appointmentTypes} = useAppointmentTypes();

// Optimistic copies of the collection fields: the chips render and compute
// from these, never from the async-refreshing prop, so rapid clicks cannot
// build a payload on a stale base and overwrite each other.
const localTypeIds = ref<number[]>([]);
const localSkills = ref<string[]>([]);
const localLaboratories = ref<ReferentLaboratory[]>([]);
const savesInFlight = ref<number>(0);
let saveQueue: Promise<unknown> = Promise.resolve();

const syncFromSchedule = () => {
    localTypeIds.value = [...(schedule?.appointment_type_ids ?? [])];
    localSkills.value = [...(schedule?.skills ?? [])];
    localLaboratories.value = [...(schedule?.referent_laboratories ?? [])];
};

watch(() => member.id, () => syncFromSchedule(), {immediate: true});

// A revalidation only reseeds the local state once every queued save is done,
// so it can never clobber a change the user just made.
watch(() => schedule, () => {
    if (!savesInFlight.value) {
        syncFromSchedule();
    }
});

const isQualified = (typeId: number): boolean => localTypeIds.value.includes(typeId);

/**
 * Toggle a habilitation: the appointment types the member is put forward for
 * wherever a collaborator is picked. Free skills never weigh in that choice.
 */
const toggleQualification = (typeId: number) => {
    localTypeIds.value = isQualified(typeId)
        ? localTypeIds.value.filter(id => id !== typeId)
        : [...localTypeIds.value, typeId];

    queueSave({appointment_type_ids: [...localTypeIds.value]}, true);
};

const updateSkills = (skills: string[]) => {
    localSkills.value = skills;
    queueSave({skills}, true);
};

const updateLaboratories = (laboratories: ReferentLaboratory[]) => {
    localLaboratories.value = laboratories;
    queueSave({referent_laboratory_ids: laboratories.map(laboratory => laboratory.id)}, true);
};

const editOpen = ref<boolean>(false);
const saving = ref<boolean>(false);
const draftContract = ref<string>('null');
const draftHours = ref<string>('');
const draftInterim = ref<boolean>(false);
const hoursError = ref<boolean>(false);

const contractOptions = [{label: 'Aucun', value: 'null'}, ...CONTRACT_TYPES.map(type => ({label: type.label, value: type.value}))];

/** The planning role follows the rank — the single "poste" field. */
const role = computed(() => jobRoleOption(schedule?.job_role ?? jobRoleForRank(member.rankId)));

const initials = computed(() => {
    const name = member.user?.name ?? '';

    return name.split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase() || '?';
});

const seedDrafts = () => {
    draftContract.value = schedule?.contract_type ?? 'null';
    draftHours.value = schedule?.weekly_hours ? String(schedule.weekly_hours) : '';
    draftInterim.value = schedule?.is_interim ?? false;
    hoursError.value = false;
};

watch(() => [member.id, editOpen.value], seedDrafts, {immediate: true});

// Opened before the profile had loaded, the dialog would save its blank
// drafts over it: they take the profile's values once it arrives.
watch(() => schedule, (current, previous) => {
    if (current && !previous) {
        seedDrafts();
    }
});

/**
 * Persist part of the planning profile, serialized: a whole-array field sent
 * by a later click always carries the earlier clicks' changes, whatever the
 * network ordering. On failure the optimistic state reverts to the prop.
 */
const queueSave = (request: PlanningProfileUpdateRequest, quiet = false) => {
    const memberId = member.id;

    savesInFlight.value++;
    saveQueue = saveQueue
        .then(() => teamPlanningApi().updateProfile(organizationId, memberId, request))
        .then(updated => {
            emits('schedule-updated', updated);

            if (!quiet) {
                showSuccess({content: 'La fiche a été mise à jour'});
            }
        })
        .catch(() => {
            showError({content: 'La fiche n\'a pas pu être mise à jour'});
            syncFromSchedule();
        })
        .finally(() => savesInFlight.value--);

    return saveQueue;
};

/**
 * The rank changed: keep the planning role in sync, silently — the access
 * update already confirmed itself.
 */
const onRankChanged = (rankId: number) => {
    queueSave({job_role: jobRoleForRank(rankId)}, true);
    emits('refresh');
};

const saveEditDialog = async () => {
    const weeklyHours = parseWeeklyHours(draftHours.value);

    if (weeklyHours === undefined) {
        hoursError.value = true;

        return;
    }

    hoursError.value = false;
    saving.value = true;

    await queueSave({
        job_role: jobRoleForRank(member.rankId),
        contract_type: draftContract.value === 'null' ? null : draftContract.value as PlanningProfileUpdateRequest['contract_type'],
        weekly_hours: weeklyHours,
        is_interim: draftInterim.value
    });

    saving.value = false;
    editOpen.value = false;
};
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div class="flex items-center gap-4">
      <div
        class="flex size-14 flex-none items-center justify-center rounded-full text-lg font-semibold text-white"
        :style="{backgroundColor: role?.color ?? '#8b93a7'}"
      >
        {{ initials }}
      </div>
      <div class="flex-1">
        <div class="text-lg font-semibold text-gray-800">
          {{ member.user?.name }}
        </div>
        <div class="mt-0.5 flex items-center gap-2">
          <span class="text-xs text-gray-500">{{ member.rank?.title ?? role?.label ?? 'Poste non renseigné' }}</span>
          <span
            v-if="schedule?.is_interim"
            class="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
          >
            Intérimaire
          </span>
          <span class="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">
            <span class="size-1.5 rounded-full bg-green-500" />
            Actif
          </span>
          <button
            v-if="rankRequiresRpps(member.rankId)"
            :title="member.cps || member.rpps ? 'Modifier le RPPS' : 'Renseigner le RPPS'"
            class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors"
            :class="member.cps || member.rpps
              ? 'bg-secondary/50 text-gray-700 hover:bg-secondary'
              : 'bg-amber-50 text-amber-700 hover:bg-amber-100'"
            @click="editOpen = true"
          >
            <BadgeCheck v-if="member.cps || member.rpps" class="size-3" />
            <ShieldAlert v-else class="size-3" />
            {{ member.cps || member.rpps ? `RPPS : ${member.cps ?? member.rpps}` : 'RPPS non renseigné' }}
          </button>
        </div>
      </div>
      <BaseButton @click="editOpen = true">
        <span class="flex items-center gap-1.5">
          <Pencil class="size-3.5" />
          Modifier
        </span>
      </BaseButton>
    </div>

    <div class="mt-4 grid grid-cols-3 gap-3">
      <div class="rounded-lg bg-gray-50 px-3.5 py-2.5">
        <div class="text-[10px] text-gray-400">
          Contrat
        </div>
        <div class="mt-0.5 text-sm font-semibold text-gray-800">
          {{ contractLabel(schedule?.contract_type ?? null) ?? '—' }}
        </div>
      </div>
      <div class="rounded-lg bg-gray-50 px-3.5 py-2.5">
        <div class="text-[10px] text-gray-400">
          Volume hebdo
        </div>
        <div class="mt-0.5 text-sm font-semibold text-gray-800">
          {{ schedule?.weekly_hours ? `${formatHours(schedule.weekly_hours * 60)} / semaine` : '—' }}
        </div>
      </div>
      <div class="rounded-lg bg-gray-50 px-3.5 py-2.5">
        <div class="text-[10px] text-gray-400">
          Code opérateur
        </div>
        <div class="mt-0.5 text-sm font-semibold text-gray-800">
          {{ member.operatorCode || '—' }}
        </div>
      </div>
    </div>

    <div class="mt-5">
      <div class="mb-2 text-xs font-semibold text-gray-500">
        Habilitations (types de rendez-vous)
      </div>
      <div class="mb-1.5 text-[10px] text-gray-400">
        Ce membre est proposé en priorité pour les types cochés. Il reste proposable pour les autres, après les membres habilités.
      </div>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="type in (appointmentTypes ?? [])"
          :key="type.id"
          class="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors"
          :class="isQualified(type.id)
            ? 'border-transparent bg-primary/10 text-primary'
            : 'border-dashed border-gray-300 text-gray-400 hover:border-primary/40 hover:text-gray-600'"
          @click="toggleQualification(type.id)"
        >
          <span class="size-2 rounded-full" :style="{backgroundColor: type.color ?? '#8b93a7'}" />
          {{ type.name }}
        </button>
      </div>
    </div>

    <div class="mt-5 border-t border-gray-100 pt-4">
      <div class="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
        <FlaskConical class="size-3.5 text-primary" />
        Référent laboratoires
      </div>
      <div class="mb-1.5 text-[10px] text-gray-400">
        Pour un rendez-vous rattaché à l'un de ces laboratoires, ce membre passe en priorité s'il est aussi habilité pour le type.
      </div>
      <LaboratoryPicker
        :model-value="localLaboratories"
        @update:model-value="updateLaboratories"
      />
    </div>

    <div class="mt-5 border-t border-gray-100 pt-4">
      <div class="mb-2 text-xs font-semibold text-gray-500">
        Autres compétences
      </div>
      <div class="mb-1.5 text-[10px] text-gray-400">
        Information libre, pour mémoire : n'intervient pas dans le choix de l'intervenant d'un rendez-vous.
      </div>
      <ChipListEditor
        :model-value="localSkills"
        placeholder="Ajouter…"
        @update:model-value="updateSkills"
      />
    </div>

    <Dialog v-model:open="editOpen">
      <DialogContent class="max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifier la fiche</DialogTitle>
        </DialogHeader>
        <div class="flex flex-col gap-4">
          <div class="grid grid-cols-2 items-end gap-4">
            <div class="flex flex-col gap-1.5">
              <Label>Contrat</Label>
              <BaseSelect
                v-model:model-value="draftContract"
                trigger-class="items-start w-full"
                :options="contractOptions"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <Label>Volume hebdo contractuel (h)</Label>
              <Input
                v-model="draftHours"
                placeholder="35"
                :class="hoursError ? 'border-red-400 focus-visible:ring-red-400' : ''"
              />
              <p v-if="hoursError" class="text-xs text-red-500">
                Nombre d'heures invalide (entre 0 et 80).
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Switch v-model="draftInterim" />
            <Label>Intérimaire</Label>
          </div>

          <MemberAccessSettings
            :member="member"
            :team="team"
            :ranks="ranks"
            :operator-code-options="operatorCodeOptions"
            @refresh="emits('refresh')"
            @rank-changed="onRankChanged"
            @on-remove-operator-code="code => emits('on-remove-operator-code', code)"
          />

          <div class="flex justify-end">
            <BaseButton primary :loading="saving" @click="saveEditDialog">
              Enregistrer
            </BaseButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
